<template>
    <div class="after-sales-workflow">
        <van-nav-bar title="售后流程处理" left-arrow @click-left="$router.back()" />

        <main class="workflow-content">
            <div v-if="loading" class="loading-state"><van-loading size="28" vertical>正在加载工单</van-loading></div>
            <template v-else-if="workflow && workflow.order">
                <section class="order-header">
                    <div>
                        <span class="eyebrow">INTERNAL AFTER-SALES</span>
                        <h1>{{ workflow.order.product_name || '售后设备' }}</h1>
                        <p>{{ workflow.order.order_no }} · {{ workflow.order.device_sn }}</p>
                    </div>
                    <van-tag :type="isFinished ? 'success' : 'primary'">{{ stageText(workflow.current_stage) }}</van-tag>
                </section>

                <section class="card process-card">
                    <van-steps :active="activeStep" active-color="#315fe8" finish-icon="success">
                        <van-step v-for="item in stages" :key="item.key">{{ item.label }}</van-step>
                    </van-steps>
                </section>

                <section class="card summary-card">
                    <div class="card-title"><strong>设备与接件信息</strong><van-tag plain type="primary">{{ workflow.device && workflow.device.internal_sn }}</van-tag></div>
                    <p class="issue-text">{{ workflow.order.issue_type }}：{{ workflow.order.description }}</p>
                    <div class="summary-grid">
                        <span><small>联系人</small>{{ workflow.order.contact_name || '-' }}</span>
                        <span><small>联系电话</small>{{ workflow.order.phone || '-' }}</span>
                        <span><small>原厂 SN</small>{{ workflow.device && workflow.device.original_sn || '无' }}</span>
                        <span><small>当前阶段</small>{{ stageText(workflow.current_stage) }}</span>
                    </div>
                </section>

                <section v-if="hasHistory" class="card history-card">
                    <div class="card-title"><strong>已录入处理记录</strong><van-tag plain type="success">可追溯</van-tag></div>
                    <div v-if="workflow.latest_diagnosis" class="history-row"><span>问题确认</span><strong>{{ diagnosisText(workflow.latest_diagnosis.result) }}</strong><small>{{ workflow.latest_diagnosis.fault_description || workflow.latest_diagnosis.cause_analysis || '已完成确认' }}</small></div>
                    <div v-if="workflow.latest_repair" class="history-row"><span>维修结果</span><strong>{{ repairText(workflow.latest_repair.repair_result) }}</strong><small>{{ workflow.latest_repair.result_description || '已记录维修过程' }}</small></div>
                    <div v-if="workflow.latest_qc" class="history-row"><span>质检结果</span><strong>{{ Number(workflow.latest_qc.passed) === 1 ? '质检通过' : '质检不通过' }}</strong><small>{{ workflow.latest_qc.test_result || workflow.latest_qc.reject_reason || '-' }}</small></div>
                    <div v-if="workflow.disposition" class="history-row"><span>处理方式</span><strong>{{ dispositionText(workflow.disposition.method) }}</strong><small>{{ workflow.disposition.reason || '-' }}</small></div>
                    <div v-if="workflow.shipments && workflow.shipments.length" class="history-row"><span>物流信息</span><strong>{{ workflow.shipments[workflow.shipments.length - 1].carrier }} {{ workflow.shipments[workflow.shipments.length - 1].tracking_no }}</strong><small>{{ workflow.shipments[workflow.shipments.length - 1].logistics_status }}</small></div>
                </section>

                <section v-if="showDiagnosis" class="card form-card">
                    <div class="card-title"><strong>问题确认</strong><span class="step-badge">01</span></div>
                    <van-radio-group v-model="diagnosis.result" direction="horizontal" class="radio-group">
                        <van-radio name="confirmed">确认故障</van-radio>
                        <van-radio name="not_reproduced">未复现</van-radio>
                        <van-radio name="external_damage">外部损坏</van-radio>
                        <van-radio name="software">软件问题</van-radio>
                        <van-radio name="other">其他</van-radio>
                    </van-radio-group>
                    <van-cell-group inset>
                        <van-field v-model.trim="diagnosis.faultCode" label="故障编码" placeholder="可选" maxlength="60" />
                        <van-field v-model.trim="diagnosis.faultDescription" label="故障描述" type="textarea" rows="3" autosize maxlength="1000" placeholder="填写确认后的故障现象" />
                        <van-field v-model.trim="diagnosis.causeAnalysis" label="原因分析" type="textarea" rows="3" autosize maxlength="2000" placeholder="填写初步原因分析" />
                        <van-field v-model.trim="diagnosis.responsibility" label="责任归属" placeholder="如：保修、客户责任、第三方、未知" maxlength="30" />
                    </van-cell-group>
                    <van-button block type="primary" :loading="submitting" @click="submitDiagnosis">提交问题确认</van-button>
                </section>

                <section v-if="showRepair" class="card form-card">
                    <div class="card-title"><strong>进入维修 / 维修结果</strong><span class="step-badge">02</span></div>
                    <div v-if="stageIs('diagnosis_completed', 'qc_failed')" class="start-repair-tip">确认设备和维修方案后，点击开始维修。</div>
                    <van-cell-group inset>
                        <van-field v-model.trim="repair.repairPlan" label="维修方案" type="textarea" rows="2" autosize maxlength="2000" placeholder="填写维修思路和方案" />
                        <van-field v-model.trim="repair.repairActions" label="维修动作" type="textarea" rows="3" autosize maxlength="3000" placeholder="填写拆机、检测、维修动作" />
                        <van-field v-model.trim="repair.partsUsed" label="使用配件" placeholder='JSON数组，如：["主板"]' maxlength="2000" />
                        <van-field v-model.trim="repair.softwareVersion" label="软件版本" placeholder="可选" maxlength="80" />
                        <van-field v-if="stageIs('repairing', 'qc_pending')" v-model.trim="repair.resultDescription" label="结果说明" type="textarea" rows="3" autosize maxlength="2000" placeholder="填写维修后的具体结果" />
                    </van-cell-group>
                    <van-radio-group v-if="stageIs('repairing', 'qc_pending')" v-model="repair.repairResult" direction="horizontal" class="radio-group">
                        <van-radio name="repaired">已修复</van-radio>
                        <van-radio name="partially_repaired">部分修复</van-radio>
                        <van-radio name="not_repaired">未修复</van-radio>
                        <van-radio name="no_fault">无故障</van-radio>
                        <van-radio name="cannot_repair">无法维修</van-radio>
                    </van-radio-group>
                    <van-button v-if="stageIs('diagnosis_completed', 'qc_failed')" block plain type="primary" :loading="submitting" @click="startRepair">开始维修</van-button>
                    <van-button v-else block type="primary" :loading="submitting" @click="finishRepair">提交维修结果</van-button>
                </section>

                <section v-if="showQc" class="card form-card">
                    <div class="card-title"><strong>维修后质检</strong><span class="step-badge">03</span></div>
                    <van-radio-group v-model="qc.passed" direction="horizontal" class="radio-group">
                        <van-radio :name="1">质检通过</van-radio>
                        <van-radio :name="0">质检不通过</van-radio>
                    </van-radio-group>
                    <van-cell-group inset>
                        <van-field v-model.trim="qc.testResult" label="测试结果" type="textarea" rows="4" autosize maxlength="2000" placeholder="填写关键测试项目和测试结果" />
                        <van-field v-if="Number(qc.passed) === 0" v-model.trim="qc.rejectReason" label="不通过原因" type="textarea" rows="3" autosize maxlength="1000" placeholder="请填写不通过原因" />
                    </van-cell-group>
                    <van-button block type="primary" :loading="submitting" @click="submitQc">提交质检结果</van-button>
                </section>

                <section v-if="showDisposition" class="card form-card">
                    <div class="card-title"><strong>处理方式</strong><span class="step-badge">04</span></div>
                    <div v-if="stageIs('waiting_ship')" class="waiting-ship-tip">当前工单仍处于待寄送状态。内部流程不要求寄送，请确认处理方式后完成工单。</div>
                    <van-radio-group v-model="disposition.method" class="choice-list">
                        <van-radio name="repair_return">维修后寄回</van-radio>
                        <van-radio name="replacement">更换设备</van-radio>
                        <van-radio name="return_as_is">原样退回</van-radio>
                        <van-radio name="vendor">返厂处理</van-radio>
                        <van-radio name="refund">退款</van-radio>
                        <van-radio name="scrap">报废</van-radio>
                    </van-radio-group>
                    <van-cell-group inset>
                        <van-field v-model.trim="disposition.reason" label="处理原因" type="textarea" rows="3" autosize maxlength="2000" placeholder="填写处理原因和依据" />
                        <van-field v-model.trim="disposition.remark" label="备注" type="textarea" rows="2" autosize maxlength="1000" placeholder="可选" />
                    </van-cell-group>
                    <van-button block type="primary" :loading="submitting" @click="submitDisposition">提交处理方式</van-button>
                </section>

                <section v-if="isFinished" class="card finished-card">
                    <van-icon name="checked" color="#07c160" size="42" />
                    <strong>售后流程已完成</strong>
                    <span>处理方式已确认，工单已关闭</span>
                </section>

                <van-button class="refresh-button" block plain type="primary" :loading="loading" @click="loadWorkflow">刷新流程状态</van-button>
            </template>
            <div v-else class="empty-state"><van-icon name="orders-o" size="42" /><p>未找到售后工单</p></div>
        </main>
    </div>
</template>

<script>
import afterSalesApi from '@/api/after_sales_internal_api.js'

const requestPayload = response => response && (response.other || response.data || response.result)

export default {
    name: 'AfterSalesWorkflowMobile',
    data() {
        return {
            loading: false,
            submitting: false,
            workflow: null,
            stages: [
                { key: 'received', label: '接件' },
                { key: 'diagnosis_completed', label: '问题确认' },
                { key: 'repairing', label: '维修' },
                { key: 'qc_passed', label: '质检' },
                { key: 'disposition', label: '处理方式' }
            ],
            diagnosis: { result: 'confirmed', faultCode: '', faultDescription: '', causeAnalysis: '', responsibility: '' },
            repair: { repairPlan: '', repairActions: '', partsUsed: '[]', softwareVersion: '', repairResult: 'repaired', resultDescription: '' },
            qc: { passed: 1, testResult: '', rejectReason: '' },
            disposition: { method: 'repair_return', reason: '', remark: '' },
            shipment: { carrier: '', trackingNo: '', recipientName: '', recipientPhone: '', recipientAddress: '', lastEvent: '', logisticsStatus: 'shipped' }
        }
    },
    computed: {
        appId() { return this.$root.app_id || '' },
        orderId() { return Number(this.$route.query.order_id || this.$route.query.id || 0) },
        activeStep() {
            const stage = this.workflow && this.workflow.current_stage
            const index = this.stages.findIndex(item => item.key === stage)
            if (stage === 'diagnosing') return 1
            if (stage === 'qc_pending' || stage === 'qc_failed') return 3
            if (stage === 'waiting_ship' || stage === 'shipped' || stage === 'signed' || stage === 'closed') return 4
            return index >= 0 ? index : 0
        },
        isFinished() { return ['signed', 'closed'].indexOf(this.workflow && this.workflow.current_stage) !== -1 },
        hasHistory() { return Boolean(this.workflow && (this.workflow.latest_diagnosis || this.workflow.latest_repair || this.workflow.latest_qc || this.workflow.disposition || (this.workflow.shipments && this.workflow.shipments.length))) },
        showDiagnosis() { return !this.workflow.latest_diagnosis || this.stageIs('received', 'diagnosing', 'diagnosis_completed', 'qc_failed') },
        showRepair() { return this.stageIs('diagnosis_completed', 'repairing', 'qc_failed') },
        showQc() { return this.stageIs('qc_pending', 'qc_failed') },
        showDisposition() {
            const diagnosis = this.workflow && this.workflow.latest_diagnosis
            return this.stageIs('qc_passed', 'disposition', 'waiting_ship') || (this.stageIs('diagnosis_completed') && diagnosis && diagnosis.result === 'not_reproduced')
        },
        showShipment() { return false }
    },
    mounted() {
        document.title = '售后流程处理'
        this.loadWorkflow()
    },
    methods: {
        stageIs(...stages) { return stages.indexOf(this.workflow && this.workflow.current_stage) !== -1 },
        stageText(stage) {
            const map = { received: '已接件', diagnosing: '问题确认中', diagnosis_completed: '待维修', repairing: '维修中', qc_pending: '待质检', qc_failed: '质检不通过', qc_passed: '质检通过', disposition: '处理中', waiting_ship: '待寄送', shipped: '已发货', signed: '已签收', closed: '已关闭' }
            return map[stage] || '处理中'
        },
        diagnosisText(result) {
            return { confirmed: '确认故障', not_reproduced: '未复现', external_damage: '外部损坏', software: '软件问题', other: '其他' }[result] || result || '-'
        },
        repairText(result) {
            return { repaired: '已修复', partially_repaired: '部分修复', not_repaired: '未修复', no_fault: '无故障', cannot_repair: '无法维修' }[result] || result || '维修中'
        },
        dispositionText(method) {
            return { repair_return: '维修后寄回', replacement: '更换设备', return_as_is: '原样退回', vendor: '返厂处理', refund: '退款', scrap: '报废' }[method] || method || '-'
        },
        async loadWorkflow() {
            if (!this.orderId) return
            this.loading = true
            try {
                const response = await afterSalesApi.getWorkflow(this.appId, this.orderId)
                const payload = requestPayload(response)
                if (!response || response.errcode !== 0 || !payload) throw new Error((response && (response.errmsg || response.msg)) || '工单加载失败')
                this.workflow = payload
                this.fillLatestValues(payload)
            } catch (error) {
                this.$toast.fail(error.message || '工单加载失败')
            } finally {
                this.loading = false
            }
        },
        fillLatestValues(payload) {
            const diagnosis = payload.latest_diagnosis || {}
            const repair = payload.latest_repair || {}
            const qc = payload.latest_qc || {}
            const disposition = payload.disposition || {}
            const shipment = (payload.shipments && payload.shipments[payload.shipments.length - 1]) || {}
            this.diagnosis = Object.assign(this.diagnosis, { result: diagnosis.result || this.diagnosis.result, faultCode: diagnosis.fault_code || '', faultDescription: diagnosis.fault_description || '', causeAnalysis: diagnosis.cause_analysis || '', responsibility: diagnosis.responsibility || '' })
            this.repair = Object.assign(this.repair, { repairPlan: repair.repair_plan || '', repairActions: repair.repair_actions || '', partsUsed: JSON.stringify(repair.parts_used || []), softwareVersion: repair.software_version || '', repairResult: repair.repair_result || this.repair.repairResult, resultDescription: repair.result_description || '' })
            this.qc = Object.assign(this.qc, { passed: qc.id ? Number(qc.passed) : 1, testResult: qc.test_result || '', rejectReason: qc.reject_reason || '' })
            this.disposition = Object.assign(this.disposition, { method: disposition.method || this.disposition.method, reason: disposition.reason || '', remark: disposition.remark || '' })
            this.shipment = Object.assign(this.shipment, { carrier: shipment.carrier || '', trackingNo: shipment.tracking_no || '', recipientName: shipment.recipient_name || '', recipientPhone: shipment.recipient_phone || '', recipientAddress: shipment.recipient_address || '', lastEvent: shipment.last_event || '', logisticsStatus: shipment.logistics_status || this.shipment.logisticsStatus })
        },
        async submitStage(apiCall, data, successText) {
            if (this.submitting) return
            this.submitting = true
            try {
                const response = await apiCall(Object.assign({ order_id: this.orderId }, data || {}))
                const payload = requestPayload(response)
                if (!response || response.errcode !== 0 || !payload) throw new Error((response && (response.errmsg || response.msg)) || '提交失败')
                this.workflow = payload
                this.fillLatestValues(payload)
                this.$toast.success(successText)
            } catch (error) {
                this.$toast.fail(error.message || '提交失败')
            } finally {
                this.submitting = false
            }
        },
        submitDiagnosis() {
            return this.submitStage(data => afterSalesApi.submitDiagnosis(this.appId, data), { result: this.diagnosis.result, fault_code: this.diagnosis.faultCode, fault_description: this.diagnosis.faultDescription, cause_analysis: this.diagnosis.causeAnalysis, responsibility: this.diagnosis.responsibility, test_items: '[]', evidence_images: '[]' }, '问题确认已保存')
        },
        startRepair() {
            return this.submitStage(data => afterSalesApi.updateRepair(this.appId, data), { action: 'start', repair_plan: this.repair.repairPlan, repair_actions: this.repair.repairActions, parts_used: this.repair.partsUsed, software_version: this.repair.softwareVersion }, '已进入维修')
        },
        finishRepair() {
            if (!this.repair.repairResult) return this.$toast('请选择维修结果')
            return this.submitStage(data => afterSalesApi.updateRepair(this.appId, data), { action: 'finish', repair_plan: this.repair.repairPlan, repair_actions: this.repair.repairActions, parts_used: this.repair.partsUsed, software_version: this.repair.softwareVersion, repair_result: this.repair.repairResult, result_description: this.repair.resultDescription }, '维修结果已保存')
        },
        submitQc() {
            if (Number(this.qc.passed) === 0 && !this.qc.rejectReason) return this.$toast('请填写质检不通过原因')
            return this.submitStage(data => afterSalesApi.submitQc(this.appId, data), { passed: Number(this.qc.passed), test_items: '[]', test_result: this.qc.testResult, qc_images: '[]', reject_reason: this.qc.rejectReason }, '质检结果已保存')
        },
        submitDisposition() {
            return this.submitStage(data => afterSalesApi.submitDisposition(this.appId, data), { method: this.disposition.method, reason: this.disposition.reason, approval_status: 'not_required', remark: this.disposition.remark }, '处理方式已保存')
        },
        submitShipment() {
            if (!this.shipment.carrier || !this.shipment.trackingNo) return this.$toast('请填写承运商和物流单号')
            return this.submitStage(data => afterSalesApi.submitShipment(this.appId, data), { direction: 'outbound', carrier: this.shipment.carrier, tracking_no: this.shipment.trackingNo, recipient_name: this.shipment.recipientName, recipient_phone: this.shipment.recipientPhone, recipient_address: this.shipment.recipientAddress, logistics_status: this.shipment.logisticsStatus, last_event: this.shipment.lastEvent, package_images: '[]', route_json: '[]' }, '物流信息已保存')
        }
    }
}
</script>

<style scoped>
.after-sales-workflow { width: 100%; height: 100%; min-height: 0; overflow-x: hidden; overflow-y: auto; -webkit-overflow-scrolling: touch; overscroll-behavior-y: contain; background: #f5f8fb; color: #1f2937; padding-bottom: 30px; }
.workflow-content { max-width: 760px; margin: 0 auto; padding: 18px 16px 46px; font-size: 18px; }
.loading-state, .empty-state { padding: 80px 0; text-align: center; color: #94a3b8; }
.empty-state p { margin-top: 12px; }
.order-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; padding: 22px 20px; border-radius: 18px; color: #fff; background: linear-gradient(135deg, #315fe8, #5b8dff); box-shadow: 0 12px 26px rgba(49,95,232,.2); }
.eyebrow { display: block; font-size: 16px; letter-spacing: 1.2px; opacity: .78; }
.order-header h1 { margin: 10px 0 7px; font-size: 36px; line-height: 1.2; }
.order-header p { margin: 0; font-size: 17px; opacity: .88; word-break: break-all; }
.card { margin-top: 16px; padding: 18px 14px; border-radius: 16px; background: #fff; box-shadow: 0 5px 18px rgba(31,41,55,.06); }
.process-card { overflow-x: auto; padding: 18px 6px 8px; }
.card-title { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; font-size: 22px; }
.step-badge { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 50%; color: #315fe8; background: #eaf0ff; font-size: 15px; }
.issue-text { margin: 0 0 18px; color: #475569; line-height: 1.7; font-size: 18px; }
.history-row { display: grid; grid-template-columns: 88px 1fr; gap: 6px 12px; padding: 15px 0; border-bottom: 1px solid #eef2f7; font-size: 18px; }
.history-row:last-child { border-bottom: 0; padding-bottom: 0; }
.history-row span { color: #64748b; }
.history-row strong { color: #1e293b; }
.history-row small { grid-column: 2; color: #94a3b8; line-height: 1.6; font-size: 16px; word-break: break-all; }
.summary-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.summary-grid span { display: flex; flex-direction: column; gap: 6px; color: #334155; font-size: 18px; word-break: break-all; }
.summary-grid small { color: #94a3b8; font-size: 16px; }
.form-card { padding-bottom: 20px; }
.form-card .van-button { margin-top: 16px; }
.radio-group { display: flex; flex-wrap: wrap; gap: 12px 16px; margin: 4px 4px 14px; }
.choice-list { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 8px; padding: 2px 4px; }
.start-repair-tip { margin: -4px 4px 16px; color: #64748b; font-size: 17px; line-height: 1.55; }
.waiting-ship-tip { margin: -4px 4px 16px; padding: 12px 14px; border-radius: 10px; color: #7c5b20; background: #fff8e8; font-size: 17px; line-height: 1.55; }
.finished-card { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 28px 16px; }
.finished-card strong { font-size: 22px; }
.finished-card span { color: #64748b; font-size: 17px; }
.refresh-button { margin-top: 18px; }
.form-card ::v-deep .van-field__label,
.form-card ::v-deep .van-field__control,
.form-card ::v-deep .van-radio__label,
.form-card ::v-deep .van-button { font-size: 18px; }
.form-card ::v-deep .van-field__label { width: 92px; font-size: 17px; }
.form-card ::v-deep .van-field__control { font-size: 18px; line-height: 1.55; }
.form-card ::v-deep .van-radio__label { font-size: 18px; line-height: 1.5; }
.form-card ::v-deep .van-cell { min-height: 58px; padding-top: 14px; padding-bottom: 14px; }
.form-card ::v-deep .van-button { min-height: 48px; }
.process-card ::v-deep .van-step__title { font-size: 16px !important; line-height: 1.4; }
.process-card ::v-deep .van-step__circle { width: 10px; height: 10px; }
.workflow-content ::v-deep .van-tag { font-size: 15px; line-height: 24px; }
.after-sales-workflow ::v-deep .van-nav-bar__title { font-size: 22px; font-weight: 600; }
.after-sales-workflow ::v-deep .van-nav-bar__arrow { font-size: 25px; }
.workflow-content ::v-deep .van-tag { padding: 4px 9px; font-size: 17px; line-height: 26px; }
.workflow-content ::v-deep .van-loading__text { font-size: 18px; }
@media (max-width: 360px) { .choice-list { grid-template-columns: 1fr; } .summary-grid { grid-template-columns: 1fr; } }
</style>
